import { Link } from 'gatsby';
import { AnchorHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { cssVar } from 'src/app/styles/helper';
import { BUTTON_SIZE_STYLES } from './ButtonSize.styles';
import { BUTTON_VARIANT_STYLES } from './ButtonVariant.styles';
import { BUTTON_ICON_SIZE_STYLES } from './ButtonIcon.styles';
import { RadiusPropKeys } from 'src/app/styles/spacing';

interface ButtonContainerProps {
  $variant: 'primary' | 'secondary' | 'tertiary' | 'text-link' | 'brand-text-link';
  $size: 'sm' | 'md' | 'lg' | 'xl';
  $radius?: RadiusPropKeys;
  $decoration?: 'none' | 'underline';
  $display?: 'inline' | 'block' | 'flex';
}

const disabledButtonStyles = css`
  background: ${cssVar('bg-disabled')};
  color: ${cssVar('fg-disabled')};
  border: 1px solid ${cssVar('bg-disabled-subtle')};
  box-shadow: ${cssVar('shadow-xs')};
  cursor: not-allowed;
  pointer-events: none;
`;

const buttonStyles = css<ButtonContainerProps>`
  outline: none;
  cursor: pointer;
  text-decoration: none;
  display: ${({ $display }) => $display || 'flex'};
  align-items: center;
  justify-content: center;
  transition: all 200ms ease-in-out;
  width: 100%;
  height: fit-content;

  font-family: ${cssVar('font-family-body')};
  font-weight: ${cssVar('font-weight-semibold')};

  ${({ $size }) => BUTTON_SIZE_STYLES[$size]}
  ${({ $variant }) => BUTTON_VARIANT_STYLES[$variant]}
  ${({ $radius }) => $radius && `border-radius: ${cssVar($radius)};`}
  ${({ $decoration }) => $decoration && `text-decoration: ${$decoration};`}

  &:disabled,
  &.disabled {
    ${disabledButtonStyles}
  }
`;

export const ButtonContainer = styled.button<ButtonContainerProps>`
  ${buttonStyles}
`;

export const InternalLinkContainer = styled(Link)<ButtonContainerProps>`
  ${buttonStyles}
`;

type ExternalLinkContainerProps = ButtonContainerProps & AnchorHTMLAttributes<HTMLAnchorElement>;

export const ExternalLinkContainer = styled.a<ExternalLinkContainerProps>`
  ${buttonStyles}
`;

export const buttonIconStyles = css<ButtonContainerProps>`
  ${buttonStyles}
  ${({ $size }) => BUTTON_ICON_SIZE_STYLES[$size]}
`;

export const ButtonIconContainer = styled.button<ButtonContainerProps>`
  ${buttonIconStyles}
`;

export const ButtonIconLinkContainer = styled(Link)<ButtonContainerProps>`
  ${buttonIconStyles}
`;

export const ButtonIconExternalLinkContainer = styled.a<ButtonContainerProps>`
  ${buttonIconStyles}
`;
